import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dropdown} from 'primereact/dropdown'
import {InputText} from 'primereact/inputtext'
import {useCallback, useEffect, useMemo} from 'react'
import PhoneInput from 'react-phone-input-2'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import useWindowSize from '../../../utils/useWindowSize'
import {addressTypeOptions, addressTypes} from '../../address/AddressConstant'
import {getListGenericAddress} from '../../address/AddressEndPoint'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import {component} from '../../form/Constant'
import {generalPractitionerKey} from '../../general-practioner/GeneralPractitionerContants'
import {
    useDetailGeneralPractitioner,
    useListGeneralPractitioners,
} from '../../general-practioner/query-keys/GeneralPractitionerKeysManager'
import {
    refetchEvent,
    removeReloadDataTable,
    tableEvent,
} from '../../table-utils/TableUtils'
import {
    generalPractitionerEvent,
    generalPractitionerEventTypes,
    getGeneralPractitionerEventKey,
    onGeneralPractitionerTableChangeRemoveListener,
} from '../general-practitioner/broadcast/GeneralPractitionerEvent'
const QueryKey = 'General-Practitioner'

const ADDRESS_PATH = 'address'

const CountryPath = '/getCountries'
const StatePath = '/getStates'
const CityPath = '/getCities'

const CountryQueryKey = 'Countries-AddressForm'
const StateQueryKey = 'States-AddressForm'
const CityQueryKey = 'Cities-AddressForm'

const StyledGeneralPractitionerUtils = ({
    data = {},
    onChange = () => {},
    shouldTextBold = false,
    isLock = true,
}) => {
    useSignals()

    //#region Import
    const width = useWindowSize()
    //#endregion

    //#region Translate
    const {
        translateKeys,
        translateCommons,
        translateAddressOptions,
        translateOptions,
    } = TranslateUtils()
    //#endregion

    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region State

    const gpData = useSignal({})
    const selected = useSignal(null)

    useEffect(() => {
        if (data && data?.id) {
            gpData.value =
                data?.generalPractitioner &&
                Object.keys(data?.generalPractitioner)?.length
                    ? {...data?.generalPractitioner}
                    : {}

            selected.value = data?.generalPractitioner?.id || null
        }
    }, [data?.generalPractitioner])

    const refCountry = useSignal(null)
    const refState = useSignal(null)
    const switchInputKeys = useSignal([])
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const {data: gpDetail} = useDetailGeneralPractitioner(
        user?.id,
        selected.value,
        !!user?.id && !!selected.value,
    )
    const {data: countryOptions} = useQuery(
        [CountryQueryKey],
        async () => await getAddressData(ADDRESS_PATH + CountryPath, user?.id),
        {
            refetchOnMount: false,
        },
    )

    const {data: stateOptions} = useQuery(
        [StateQueryKey, refCountry.value, switchInputKeys.value],
        async () => await getAddressData(ADDRESS_PATH + StatePath, user?.id),
        {
            enabled:
                !!refCountry.value &&
                !switchInputKeys.value.includes('refState'),
            refetchOnMount: false,
        },
    )

    const {data: cityOptions} = useQuery(
        [CityQueryKey, refCountry.value, refState.value, switchInputKeys.value],
        async () => await getAddressData(ADDRESS_PATH + CityPath, user?.id),
        {
            enabled:
                !!refCountry.value &&
                !!refState.value &&
                !switchInputKeys.value.includes('city') &&
                !switchInputKeys.value.includes('refState'),
            refetchOnMount: false,
        },
    )

    const {data: gpOptions, refetch} = useListGeneralPractitioners(
        user?.id,
        true,
        {},
        !!user?.id,
    )

    const getAddressData = async (path, userId) => {
        return await getListGenericAddress(
            path,
            userId,
            refCountry.value,
            refState.value,
        )
    }
    //#endregion

    //#region Memo
    const addressList = useMemo(() => {
        return [
            {
                key: generalPractitionerKey.gpId,
                component: component.dropdown,
                label: 'Select GP',
                placeholder: translateKeys('Select from options'),
                options: gpOptions,
                isHide: isLock,
            },
            {
                key: generalPractitionerKey.name,
                component: component.inputText,
                label: 'GP Name',
            },

            {
                key: generalPractitionerKey.email,
                component: component.inputText,
                label: 'GP Email',
            },
            {
                key: generalPractitionerKey.surgery,
                component: component.inputText,
                label: 'GP Surgery',
            },
            {
                key: 'type',
                label: 'Address Type',
                placeholder: translateCommons('Optional'),
                component: component.dropdown,
                options: translateOptions(addressTypeOptions),
                defaultValue: addressTypes.UNKNOWN.value,
            },
            {
                key: 'refCountry',
                label: 'GP Country',
                placeholder: translateCommons('Optional'),
                component: component.dropdown,
                options: translateAddressOptions(countryOptions, 'country'),
            },
            {
                key: 'refState',
                label: 'GP State',
                placeholder: translateCommons('Optional'),
                component: component.dropdown,
                allowInput: true,
                options: translateAddressOptions(stateOptions, 'state'),
            },
            {
                key: 'city',
                label: 'GP City',
                placeholder: translateCommons('Optional'),
                component: component.dropdown,
                allowInput: true,
                options: translateAddressOptions(cityOptions, 'city'),
            },
            {
                key: 'addressLine1',
                label: 'GP Address Line 1',
                placeholder: translateCommons('Optional'),
                component: component.inputText,
            },
            {
                key: 'addressLine2',
                label: 'GP Address Line 2',
                placeholder: translateCommons('Optional'),
                component: component.inputText,
            },
            {
                key: 'addressLine3',
                label: 'GP Address Line 3',
                placeholder: translateCommons('Optional'),
                component: component.inputText,
            },
            {
                key: 'addressLine4',
                label: 'GP Address Line 4',
                placeholder: translateCommons('Optional'),
                component: component.inputText,
            },
            {
                key: 'postalCode',
                label: 'GP Postal Code',
                placeholder: translateCommons('Optional'),
                component: component.inputText,
            },
            {
                key: 'phone',
                label: 'GP Phone',
                component: component.phoneInput,
            },
        ]
    }, [
        translateKeys,
        gpOptions,
        translateCommons,
        translateOptions,
        translateAddressOptions,
        countryOptions,
        stateOptions,
        cityOptions,
        isLock,
    ])
    //#endregion

    //#region Handle Function
    const handleSwitchInput = useCallback(
        key => {
            let onChangeValue = {}

            if (switchInputKeys.value?.includes(key)) {
                const inputKeys = switchInputKeys.value.filter(x => x !== key)

                switchInputKeys.value = inputKeys
                if (key === 'refState') {
                    refState.value = null
                    onChangeValue = {
                        ...gpData.value,
                        refState: null,
                        state: '',
                        city: null,
                        addressConfig: {
                            inputFields: [...inputKeys],
                        },
                    }
                } else if (key === 'city') {
                    onChangeValue = {
                        ...gpData.value,
                        city: null,
                        addressConfig: {
                            inputFields: [...inputKeys],
                        },
                    }
                }
            } else {
                if (
                    key === 'refState' &&
                    !switchInputKeys.value.includes('city')
                ) {
                    const switchInputKeysValue = [
                        ...switchInputKeys.value,
                        key,
                        'city',
                    ]

                    onChangeValue = {
                        ...gpData.value,
                        refState: null,
                        state: '',
                        city: null,
                        addressConfig: {
                            inputFields: switchInputKeysValue,
                        },
                    }
                } else if (key === 'city') {
                    switchInputKeys.value = [...switchInputKeys.value, key]
                    onChangeValue = {
                        ...gpData.value,
                        city: null,
                        addressConfig: {
                            inputFields: [...switchInputKeys.value, key],
                        },
                    }
                }
            }

            onChange(onChangeValue)
            gpData.value = onChangeValue
        },
        [switchInputKeys, onChange, gpData, refState],
    )

    const handleDropdownChange = useCallback(
        (key, value) => {
            if (key === 'gpId') {
                if (value) {
                    selected.value = value
                } else {
                    selected.value = null
                    onChange({})
                    gpData.value = {}
                }
            } else if (key === 'refCountry') {
                refCountry.value = value
                refState.value = null
                onChange({...gpData.value, [key]: value})
                gpData.value = {
                    ...gpData.value,
                    [key]: value,
                    refState: null,
                    city: null,
                }
            } else if (key === 'refState') {
                refState.value = value

                let stateLabel =
                    stateOptions &&
                    stateOptions?.length &&
                    stateOptions?.find(
                        item => item?.value === gpDetail?.refState,
                    )?.label

                onChange({
                    ...gpData.value,
                    [key]: value,
                    state: switchInputKeys.value.includes('refState')
                        ? refState.value
                        : stateLabel,
                    city: null,
                })
                gpData.value = {
                    ...gpData.value,
                    [key]: value,
                    state: switchInputKeys.value.includes('refState')
                        ? refState.value
                        : stateLabel,
                    city: null,
                }
            } else {
                onChange({...gpData.value, [key]: value})
                gpData.value = {
                    ...gpData.value,
                    [key]: value,
                }
            }
        },
        [
            gpDetail,
            gpData,
            onChange,
            refCountry,
            refState,
            selected,
            stateOptions,
            switchInputKeys.value,
        ],
    )

    const {mutate: handleReload} = useMutation(data => data, {
        onSuccess: data => {
            if (!!data?.id) {
                queryClient.setQueryData([QueryKey], oldData => [
                    {
                        value: data?.id,
                        label: data?.name,
                    },
                    ...oldData,
                ])
            }
        },
    })

    const handleReloadData = useCallback(
        data => {
            if (!!data?.id) {
                handleReload(data)
            }
        },
        [handleReload],
    )
    //#endregion

    //#region Effect
    // Effect use for select gp from options dropdown
    useEffect(() => {
        if (!!gpDetail && !!selected.value) {
            if (gpDetail?.id === selected.value) {
                let dataSelected = {
                    ...gpDetail,
                    gpId: gpDetail?.id,
                    refState:
                        gpDetail?.addressConfig &&
                        gpDetail.addressConfig?.inputFields?.length > 0 &&
                        gpDetail.addressConfig?.inputFields?.includes(
                            'refState',
                        )
                            ? gpDetail?.state
                            : gpDetail?.refState,
                }
                gpData.value = {...dataSelected}
                onChange({
                    ...dataSelected,
                })

                switchInputKeys.value =
                    gpDetail?.addressConfig &&
                    gpDetail.addressConfig?.inputFields
                        ? [...gpDetail?.addressConfig.inputFields]
                        : []
                refCountry.value =
                    gpDetail && gpDetail.refCountry ? gpDetail.refCountry : ''
                refState.value =
                    gpDetail?.addressConfig &&
                    gpDetail.addressConfig?.inputFields?.length > 0 &&
                    gpDetail.addressConfig?.inputFields?.includes('refState')
                        ? gpDetail?.state
                        : gpDetail?.refState
            }
        } else {
            return
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gpDetail, selected.value])

    useEffect(() => {
        if (!!QueryKey) {
            tableEvent.on('RELOAD-DATA-TABLE-' + QueryKey, handleReloadData)
            refetchEvent.on('REFETCH-DATA-TABLE-' + QueryKey, refetch)

            return () => {
                removeReloadDataTable(QueryKey)
                refetchEvent.removeListener('REFETCH-DATA-TABLE-' + QueryKey)
            }
        }
    }, [refetch, handleReloadData])
    // #endregion

    //#region Render
    const renderItem = useCallback(
        itemList => {
            return (
                <div className="col-12 grid align-items-stretch p-0">
                    {itemList?.map(
                        item =>
                            !item?.isHide && (
                                <div
                                    key={item.key}
                                    className={`${'col-12 md:col-4 p-0 pr-2 pb-3 '}`}>
                                    <>
                                        {/* Component Header */}
                                        <div className="flex align-items-center justify-content-between">
                                            <div>
                                                <h6 className="text-600 mb-2 font-bold">
                                                    {item?.label}
                                                </h6>
                                            </div>
                                        </div>
                                    </>

                                    {/* Dropdown */}
                                    {item.component === component.dropdown &&
                                        !switchInputKeys.value?.includes(
                                            item?.key,
                                        ) &&
                                        (!isLock ? (
                                            <div
                                                className={`flex gap-2 text-900 col-12 m-0 p-0 line-height-3`}>
                                                <div
                                                    className={`flex-grow-1 p-0`}>
                                                    <Dropdown
                                                        className={`${
                                                            item.className
                                                        }  col-12 h-3rem p-0 ${
                                                            shouldTextBold
                                                                ? 'font-semibold'
                                                                : ''
                                                        }`}
                                                        inputId={item.key}
                                                        options={
                                                            item.options || []
                                                        }
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        filter
                                                        emptyFilterMessage={translateCommons(
                                                            'No available options',
                                                        )}
                                                        emptyMessage={translateCommons(
                                                            'No results found',
                                                        )}
                                                        showClear
                                                        disabled={
                                                            item.key !==
                                                                'gpId' &&
                                                            selected.value
                                                                ? true
                                                                : false
                                                        }
                                                        filterBy="label"
                                                        value={
                                                            gpData.value
                                                                ? item.key ===
                                                                  'gpId'
                                                                    ? gpData
                                                                          .value
                                                                          ?.id
                                                                    : gpData
                                                                          .value[
                                                                          item
                                                                              .key
                                                                      ]
                                                                : null
                                                        }
                                                        onChange={e =>
                                                            handleDropdownChange(
                                                                item.key,
                                                                e.value,
                                                            )
                                                        }
                                                        placeholder={translateKeys(
                                                            item.placeholder,
                                                        )}
                                                        style={{
                                                            '--inputColor':
                                                                'rgba(68, 184, 166, 0.1)',
                                                        }}
                                                    />
                                                    {/* <span className="bar"></span>
                                                    <label htmlFor={item.key}>
                                                        {translateKeys(
                                                            item.label,
                                                        )}
                                                    </label> */}
                                                </div>
                                                {item?.allowInput && (
                                                    <div className="flex-none align-items-center justify-content-between p-0">
                                                        <Button
                                                            disabled={
                                                                item.key !==
                                                                    'gpId' &&
                                                                selected.value
                                                                    ? true
                                                                    : false
                                                            }
                                                            icon={
                                                                switchInputKeys.value?.includes(
                                                                    item?.key,
                                                                )
                                                                    ? 'pi pi-list'
                                                                    : 'pi pi-pencil'
                                                            }
                                                            className="p-button-plain p-button-rounded"
                                                            onClick={() =>
                                                                handleSwitchInput(
                                                                    item?.key,
                                                                )
                                                            }
                                                            tooltip={
                                                                switchInputKeys.value?.includes(
                                                                    item?.key,
                                                                )
                                                                    ? translateCommons(
                                                                          'Switch to dropdown',
                                                                      )
                                                                    : translateCommons(
                                                                          'Switch to input',
                                                                      )
                                                            }
                                                            tooltipOptions={{
                                                                position: 'top',
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div
                                                style={{
                                                    overflowWrap: 'break-word',
                                                    whiteSpace: 'pre-line',
                                                }}>
                                                <InputText
                                                    className="w-full h-3rem"
                                                    value={
                                                        item.options?.length
                                                            ? gpData.value[
                                                                  item?.key
                                                              ]
                                                                ? item.options.find(
                                                                      childItem =>
                                                                          childItem?.value ===
                                                                          gpData
                                                                              .value[
                                                                              item
                                                                                  ?.key
                                                                          ],
                                                                  )?.label
                                                                : item.options.find(
                                                                      childItem =>
                                                                          childItem?.value ===
                                                                          item?.defaultValue,
                                                                  )?.label ||
                                                                  'Unknown'
                                                            : 'Unknown'
                                                    }
                                                    disabled={true}
                                                />
                                            </div>
                                        ))}

                                    {/* Input Text / Input Number  */}
                                    {(item.component === component.inputText ||
                                        item.component ===
                                            component.inputNumber ||
                                        switchInputKeys.value?.includes(
                                            item?.key,
                                        )) &&
                                        (!isLock ? (
                                            <div
                                                className={`flex gap-2 text-900 col-12 m-0 p-0 line-height-3`}>
                                                <div
                                                    className={`flex-grow-1  p-0`}>
                                                    <InputText
                                                        className={` h-3rem col-12 ${
                                                            shouldTextBold
                                                                ? 'font-semibold'
                                                                : ''
                                                        }`}
                                                        value={
                                                            gpData.value &&
                                                            gpData.value[
                                                                item.key
                                                            ]
                                                                ? gpData.value[
                                                                      item.key
                                                                  ]
                                                                : ''
                                                        }
                                                        placeholder=""
                                                        disabled={
                                                            selected.value
                                                        }
                                                        onChange={e =>
                                                            handleDropdownChange(
                                                                item.key,
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                    {/* <span className="bar"></span>
                                                    <label htmlFor={item.key}>
                                                        {translateKeys(
                                                            item.label,
                                                        )}
                                                    </label> */}
                                                </div>
                                                {item?.allowInput && (
                                                    <div className="flex-none align-items-center  p-0">
                                                        <Button
                                                            disabled={
                                                                item.key !==
                                                                    'gpId' &&
                                                                selected.value
                                                            }
                                                            icon={
                                                                switchInputKeys.value?.includes(
                                                                    item?.key,
                                                                )
                                                                    ? 'pi pi-list'
                                                                    : 'pi pi-pencil'
                                                            }
                                                            className="p-button-plain p-button-rounded"
                                                            onClick={() =>
                                                                handleSwitchInput(
                                                                    item?.key,
                                                                )
                                                            }
                                                            tooltip={
                                                                switchInputKeys.value?.includes(
                                                                    item?.key,
                                                                )
                                                                    ? translateCommons(
                                                                          'Switch to dropdown',
                                                                      )
                                                                    : translateCommons(
                                                                          'Switch to input',
                                                                      )
                                                            }
                                                            tooltipOptions={{
                                                                position: 'top',
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <InputText
                                                className="w-full h-3rem"
                                                value={
                                                    gpData.value &&
                                                    gpData.value[item.key]
                                                        ? gpData.value[item.key]
                                                        : 'Unknown'
                                                }
                                                disabled={true}
                                            />
                                        ))}

                                    {/* Phone Input */}
                                    {item.component === component.phoneInput &&
                                        (!isLock ? (
                                            <div
                                                key={item.key}
                                                className="text-900 col-12 m-0 p-0 line-height-3">
                                                <span className={`col-12 p-0 `}>
                                                    {/* <label
                                                        className="text-sm text-gray-700 absolute"
                                                        style={{
                                                            marginTop: '-35px',
                                                        }}
                                                        htmlFor={item.key}>
                                                        {translateKeys(
                                                            item.label,
                                                        )}
                                                    </label> */}
                                                    <div className="col-12 m-0 p-0">
                                                        <PhoneInput
                                                            id={item.key}
                                                            className={`col-12 h-3rem p-0 ${
                                                                shouldTextBold
                                                                    ? 'font-semibold'
                                                                    : ''
                                                            }`}
                                                            inputClass={`${
                                                                shouldTextBold
                                                                    ? 'font-semibold'
                                                                    : ''
                                                            }`}
                                                            placeholder={translateKeys(
                                                                item.label,
                                                            )}
                                                            enableSearch
                                                            country={'gb'}
                                                            disabled={
                                                                !!selected.value
                                                            }
                                                            preferredCountries={[
                                                                'gb',
                                                                'us',
                                                                'ca',
                                                                'vn',
                                                            ]}
                                                            inputStyle={{
                                                                minHeight:
                                                                    '40px',
                                                            }}
                                                            alwaysDefaultMask={
                                                                true
                                                            }
                                                            onChange={(
                                                                value,
                                                                country,
                                                                e,
                                                                formattedData,
                                                            ) => {
                                                                handleDropdownChange(
                                                                    item.key,
                                                                    formattedData,
                                                                )

                                                                if (
                                                                    item.key ===
                                                                    'phone'
                                                                ) {
                                                                    handleDropdownChange(
                                                                        'phoneExtension',
                                                                        {
                                                                            countryCode:
                                                                                country?.countryCode,
                                                                            dialCode:
                                                                                country?.dialCode,
                                                                        },
                                                                    )
                                                                }
                                                            }}
                                                            value={
                                                                gpData.value
                                                                    ? gpData
                                                                          .value[
                                                                          item
                                                                              .key
                                                                      ]
                                                                    : ''
                                                            }
                                                        />
                                                    </div>
                                                </span>
                                            </div>
                                        ) : (
                                            <InputText
                                                className="w-full h-3rem"
                                                value={
                                                    gpData.value &&
                                                    gpData.value[item.key]
                                                        ? gpData.value[item.key]
                                                        : 'Unknown'
                                                }
                                                disabled={true}
                                            />
                                        ))}
                                </div>
                            ),
                    )}
                </div>
            )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            isLock,
            switchInputKeys.value,
            shouldTextBold,
            translateCommons,
            selected.value,
            gpData.value,
            translateKeys,
        ],
    )
    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        // TABLE
        generalPractitionerEvent.on(
            getGeneralPractitionerEventKey(
                generalPractitionerEventTypes.TABLE_CHANGE,
            ),
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                const {generalPractitionerKeys} = QueryKeyManager(
                    QueryKeyTypes.generalPractitioner,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: generalPractitionerKeys.pattern(),
                })

                queryClient.setQueryData(
                    generalPractitionerKeys.detail({id: selected.value}),
                    () => {},
                )
            },
        )
        return () => {
            onGeneralPractitionerTableChangeRemoveListener()
        }
    }, [])

    return <>{renderItem(addressList)}</>
}

export default StyledGeneralPractitionerUtils
